import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  WriteBatch,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { PrivateWaypoint, WaypointCategory, GeoCoordinate } from '../types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { waypointCache } from './waypointCache';

const WAYPOINTS_COLLECTION = 'privateWaypoints';
const CACHE_KEY = 'private_waypoints_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
const PAGE_SIZE = 20; // Fetch 20 waypoints at a time for better performance

interface CachedWaypoints {
  waypoints: PrivateWaypoint[];
  timestamp: number;
  hasMore: boolean;
}

interface WaypointSearchOptions {
  searchTerm?: string;
  category?: WaypointCategory | 'all';
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  limit?: number;
  offset?: number;
  lastDoc?: any; // Firebase document for pagination
}

export class PrivateWaypointsService {
  private static cachedWaypoints: PrivateWaypoint[] | null = null;
  private static loadingPromise: Promise<PrivateWaypoint[]> | null = null;
  private static lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private static hasMoreWaypoints: boolean = true;
  
  private static getUserId(): string {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return user.uid;
  }

  private static cleanDataForFirebase(data: any): any {
    const cleaned: any = {};
    for (const key in data) {
      if (data[key] !== undefined && data[key] !== null) {
        if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
          const cleanedNested = this.cleanDataForFirebase(data[key]);
          if (Object.keys(cleanedNested).length > 0) {
            cleaned[key] = cleanedNested;
          }
        } else {
          cleaned[key] = data[key];
        }
      }
    }
    return cleaned;
  }

  static async createWaypoint(
    waypoint: Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
  ): Promise<PrivateWaypoint> {
    try {
      const userId = this.getUserId();
      const waypointId = doc(collection(db, WAYPOINTS_COLLECTION)).id;
      const now = new Date().toISOString();

      const newWaypoint: PrivateWaypoint = {
        ...waypoint,
        id: waypointId,
        userId,
        createdAt: now,
        updatedAt: now,
      };

      const cleanedWaypoint = this.cleanDataForFirebase(newWaypoint);
      await setDoc(doc(db, WAYPOINTS_COLLECTION, waypointId), cleanedWaypoint);
      
      // Clear cache to force reload on next access
      this.clearCache();
      
      return newWaypoint;
    } catch (error) {
      console.error('Error creating waypoint:', error);
      throw error;
    }
  }

  static async getAllWaypointsOptimized(): Promise<PrivateWaypoint[]> {
    try {
      // Check local cache first
      const cached = await this.loadFromCache();
      if (cached && cached.length > 0) {
        console.log(`Loaded ${cached.length} waypoints from cache`);
        return cached;
      }

      // Load ALL waypoints from Firestore using pagination
      const userId = this.getUserId();
      const allWaypoints: PrivateWaypoint[] = [];
      let lastDoc: any = null;
      let hasMore = true;
      const BATCH_SIZE = 500; // Firestore allows up to 500 docs per query
      
      while (hasMore) {
        let q;
        if (lastDoc) {
          q = query(
            collection(db, WAYPOINTS_COLLECTION),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(BATCH_SIZE)
          );
        } else {
          q = query(
            collection(db, WAYPOINTS_COLLECTION),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(BATCH_SIZE)
          );
        }
        
        const snapshot = await getDocs(q);
        
        if (snapshot.docs.length < BATCH_SIZE) {
          hasMore = false;
        }
        
        if (snapshot.docs.length > 0) {
          lastDoc = snapshot.docs[snapshot.docs.length - 1];
          snapshot.docs.forEach(doc => {
            allWaypoints.push(doc.data() as PrivateWaypoint);
          });
        } else {
          hasMore = false;
        }
      }
      
      console.log(`Loaded ${allWaypoints.length} waypoints from Firestore`);
      
      // Cache for next time
      if (allWaypoints.length > 0) {
        setTimeout(() => {
          this.saveToCache(allWaypoints).catch(console.error);
        }, 100);
      }
      
      return allWaypoints;
    } catch (error) {
      console.error('Error loading waypoints:', error);
      // Try to return cached data on error
      const cached = await this.loadFromCache();
      return cached || [];
    }
  }

  static async getWaypoints(): Promise<PrivateWaypoint[]> {
    // Only load all waypoints when specifically needed (e.g., export)
    // For normal operations, use searchWaypoints with pagination
    
    // Return cached waypoints if available and not expired
    if (this.cachedWaypoints) {
      return this.cachedWaypoints;
    }

    // If already loading, wait for the current load to complete
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Start loading
    this.loadingPromise = this.loadAllWaypoints();
    try {
      this.cachedWaypoints = await this.loadingPromise;
      return this.cachedWaypoints;
    } finally {
      this.loadingPromise = null;
    }
  }

  private static async loadAllWaypoints(): Promise<PrivateWaypoint[]> {
    try {
      // Try to load from cache first
      const cached = await this.loadFromCache();
      if (cached) {
        console.log(`Loaded ${cached.length} waypoints from cache`);
        return cached;
      }

      // Load from Firestore with pagination
      const waypoints = await this.loadWaypointsFromFirestore();
      
      // Cache the results asynchronously
      setTimeout(() => {
        this.saveToCache(waypoints).catch(console.error);
      }, 100);
      
      return waypoints;
    } catch (error) {
      console.error('Error loading waypoints:', error);
      throw error;
    }
  }

  private static async loadWaypointsFromFirestore(): Promise<PrivateWaypoint[]> {
    try {
      const userId = this.getUserId();
      const allWaypoints: PrivateWaypoint[] = [];
      let lastDocument: QueryDocumentSnapshot<DocumentData> | null = null;
      let hasMore = true;
      
      while (hasMore) {
        let q = query(
          collection(db, WAYPOINTS_COLLECTION),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
        
        if (lastDocument) {
          q = query(
            collection(db, WAYPOINTS_COLLECTION),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            startAfter(lastDocument),
            limit(PAGE_SIZE)
          );
        }
        
        const querySnapshot = await getDocs(q);
        const waypoints = querySnapshot.docs.map(doc => doc.data() as PrivateWaypoint);
        allWaypoints.push(...waypoints);
        
        if (querySnapshot.docs.length < PAGE_SIZE) {
          hasMore = false;
        } else {
          lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
        }
      }
      
      console.log(`Loaded ${allWaypoints.length} waypoints from Firestore`);
      return allWaypoints;
    } catch (error) {
      console.error('Error fetching waypoints from Firestore:', error);
      throw error;
    }
  }

  private static async loadFromCache(): Promise<PrivateWaypoint[] | null> {
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const data: CachedWaypoints = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid
        if (now - data.timestamp < CACHE_EXPIRY) {
          return data.waypoints;
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading from cache:', error);
      return null;
    }
  }

  private static async saveToCache(waypoints: PrivateWaypoint[]): Promise<void> {
    try {
      const cacheData: CachedWaypoints = {
        waypoints,
        timestamp: Date.now(),
        hasMore: false
      };
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      console.log('Waypoints cached successfully');
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  }

  static async searchWaypoints(options: WaypointSearchOptions = {}): Promise<{
    waypoints: PrivateWaypoint[];
    total: number;
    hasMore: boolean;
  }> {
    const {
      searchTerm = '',
      category = 'all',
      bounds,
      limit: searchLimit = PAGE_SIZE,
      offset = 0,
      lastDoc
    } = options;

    // Calculate page number for caching
    const pageNumber = Math.floor(offset / searchLimit);
    
    // Try to get from cache first - return immediately if cached
    const cachedData = await waypointCache.get(searchTerm, category, pageNumber);
    if (cachedData && !bounds) { // Don't use cache if bounds filtering is needed
      console.log('Returning cached waypoints for page', pageNumber);
      return {
        waypoints: cachedData,
        total: cachedData.length,
        hasMore: cachedData.length === searchLimit
      };
    }

    try {
      const userId = this.getUserId();
      
      // Build Firebase query directly for better performance
      let constraints: any[] = [
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(searchLimit)
      ];

      // Add category filter to query if needed
      if (category !== 'all') {
        constraints.splice(1, 0, where('category', '==', category));
      }

      // Add pagination
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(collection(db, WAYPOINTS_COLLECTION), ...constraints);
      const querySnapshot = await getDocs(q);
      
      let waypoints = querySnapshot.docs.map(doc => doc.data() as PrivateWaypoint);
      
      // Apply client-side filters for search and bounds
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        waypoints = waypoints.filter(wp => 
          wp.name.toLowerCase().includes(term) ||
          wp.description?.toLowerCase().includes(term) ||
          wp.tags?.some(tag => tag.toLowerCase().includes(term))
        );
      }

      if (bounds) {
        waypoints = waypoints.filter(wp => 
          wp.coordinates.latitude >= bounds.south &&
          wp.coordinates.latitude <= bounds.north &&
          wp.coordinates.longitude >= bounds.west &&
          wp.coordinates.longitude <= bounds.east
        );
      }

      const hasMore = querySnapshot.docs.length === searchLimit;
      this.lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

      // Cache the results if no bounds filtering
      if (!bounds && waypoints.length > 0) {
        waypointCache.set(searchTerm, category, pageNumber, waypoints).catch(console.error);
      }

      return {
        waypoints,
        total: waypoints.length,
        hasMore
      };
    } catch (error) {
      console.error('Error searching waypoints:', error);
      // Fallback to cached data if available
      const cached = await this.loadFromCache();
      if (cached) {
        let filtered = cached;
        
        if (category !== 'all') {
          filtered = filtered.filter(wp => wp.category === category);
        }
        
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          filtered = filtered.filter(wp => 
            wp.name.toLowerCase().includes(term) ||
            wp.description?.toLowerCase().includes(term) ||
            wp.tags?.some(tag => tag.toLowerCase().includes(term))
          );
        }
        
        const paginatedWaypoints = filtered.slice(offset, offset + searchLimit);
        const hasMore = offset + searchLimit < filtered.length;
        
        return {
          waypoints: paginatedWaypoints,
          total: filtered.length,
          hasMore
        };
      }
      throw error;
    }
  }

  static clearCache(): void {
    this.cachedWaypoints = null;
    this.loadingPromise = null;
    this.lastDoc = null;
    this.hasMoreWaypoints = true;
    AsyncStorage.removeItem(CACHE_KEY).catch(console.error);
    waypointCache.clearCache().catch(console.error);
  }

  static async getWaypoint(waypointId: string): Promise<PrivateWaypoint | null> {
    try {
      const userId = this.getUserId();
      const docRef = doc(db, WAYPOINTS_COLLECTION, waypointId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const waypoint = docSnap.data() as PrivateWaypoint;
      
      // Verify ownership
      if (waypoint.userId !== userId) {
        throw new Error('Unauthorized access to waypoint');
      }

      return waypoint;
    } catch (error) {
      console.error('Error fetching waypoint:', error);
      throw error;
    }
  }

  static async updateWaypoint(
    waypointId: string,
    updates: Partial<Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt'>>
  ): Promise<void> {
    try {
      const userId = this.getUserId();
      
      // Verify ownership
      const existingWaypoint = await this.getWaypoint(waypointId);
      if (!existingWaypoint) {
        throw new Error('Waypoint not found');
      }

      const updateData = this.cleanDataForFirebase({
        ...updates,
        updatedAt: new Date().toISOString(),
      });
      
      await updateDoc(doc(db, WAYPOINTS_COLLECTION, waypointId), updateData);
      
      // Clear cache to force reload on next access
      this.clearCache();
    } catch (error) {
      console.error('Error updating waypoint:', error);
      throw error;
    }
  }

  static async deleteWaypoint(waypointId: string): Promise<void> {
    try {
      const userId = this.getUserId();
      
      // Verify ownership
      const existingWaypoint = await this.getWaypoint(waypointId);
      if (!existingWaypoint) {
        throw new Error('Waypoint not found');
      }

      await deleteDoc(doc(db, WAYPOINTS_COLLECTION, waypointId));
      
      // Clear cache to force reload on next access
      this.clearCache();
    } catch (error) {
      console.error('Error deleting waypoint:', error);
      throw error;
    }
  }

  static async getWaypointsByCategory(category: WaypointCategory): Promise<PrivateWaypoint[]> {
    try {
      const userId = this.getUserId();
      const q = query(
        collection(db, WAYPOINTS_COLLECTION),
        where('userId', '==', userId),
        where('category', '==', category),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data() as PrivateWaypoint);
    } catch (error) {
      console.error('Error fetching waypoints by category:', error);
      throw error;
    }
  }


  static async getNearbyWaypoints(
    center: GeoCoordinate,
    radiusKm: number
  ): Promise<PrivateWaypoint[]> {
    try {
      const waypoints = await this.getWaypoints();

      return waypoints.filter(wp => {
        const distance = this.calculateDistance(center, wp.coordinates);
        return distance <= radiusKm;
      });
    } catch (error) {
      console.error('Error fetching nearby waypoints:', error);
      throw error;
    }
  }

  static async getAllWaypointsForUser(userId: string): Promise<PrivateWaypoint[]> {
    try {
      console.log(`[PrivateWaypointsService] Loading all waypoints for user ${userId}`);

      // Query Firestore directly with the provided userId
      const waypointsQuery = query(
        collection(db, WAYPOINTS_COLLECTION),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(waypointsQuery);
      const allWaypoints = querySnapshot.docs.map(doc => doc.data() as PrivateWaypoint);

      console.log(`[PrivateWaypointsService] Found ${allWaypoints.length} total waypoints for user`);
      return allWaypoints;
    } catch (error) {
      console.error('Error fetching waypoints for user:', error);
      // Return empty array instead of throwing
      return [];
    }
  }

  static async getNearbyWaypointsForUser(
    userId: string,
    center: GeoCoordinate,
    radiusKm: number
  ): Promise<PrivateWaypoint[]> {
    try {
      console.log(`[PrivateWaypointsService] Fetching nearby waypoints for user ${userId}`);

      // Get all waypoints for the user
      const allWaypoints = await this.getAllWaypointsForUser(userId);

      // Filter by distance
      const nearbyWaypoints = allWaypoints.filter(wp => {
        const distance = this.calculateDistance(center, wp.coordinates);
        return distance <= radiusKm;
      });

      console.log(`[PrivateWaypointsService] ${nearbyWaypoints.length} waypoints within ${radiusKm}km radius`);

      return nearbyWaypoints;
    } catch (error) {
      console.error('Error fetching nearby waypoints for user:', error);
      // Return empty array instead of throwing
      return [];
    }
  }

  private static calculateDistance(coord1: GeoCoordinate, coord2: GeoCoordinate): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(coord2.latitude - coord1.latitude);
    const dLon = this.toRad(coord2.longitude - coord1.longitude);
    const lat1 = this.toRad(coord1.latitude);
    const lat2 = this.toRad(coord2.latitude);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  private static toRad(deg: number): number {
    return deg * (Math.PI/180);
  }

  static async importWaypoints(waypoints: Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'>[]): Promise<void> {
    try {
      const userId = this.getUserId();
      const batch = writeBatch(db);
      const now = new Date().toISOString();

      waypoints.forEach(waypoint => {
        const waypointId = doc(collection(db, WAYPOINTS_COLLECTION)).id;
        const newWaypoint: PrivateWaypoint = {
          ...waypoint,
          id: waypointId,
          userId,
          createdAt: now,
          updatedAt: now,
        };
        
        const docRef = doc(db, WAYPOINTS_COLLECTION, waypointId);
        batch.set(docRef, newWaypoint);
      });

      await batch.commit();
      
      // Clear cache to force reload on next access
      this.clearCache();
    } catch (error) {
      console.error('Error importing waypoints:', error);
      throw error;
    }
  }
}